import { getContext } from '@/context';
import {
  getPickerResourceUrl,
  isPickerResourceVideo,
} from '@/utils/picker/pickerUtils';

import type { PickerResource } from '@/types';

type NativePickerOptions = {
  allowedFormats: ('photo' | 'video')[];
  multiSelect?: boolean;
  maxCount?: number;
};

type NativePickerFile = {
  id: string;
  url: string;
  type: 'photo' | 'video';
};

type NativePickerResult = {
  files: NativePickerFile[];
};

/**
 * Opens the native Picsart photo/video picker on iOS/Android.
 * Uses the SDK's openFileChooser handler.
 *
 * @param options - Picker configuration
 * @returns Selected files or null if cancelled/failed
 */
const openNativePicker = async (
  options: NativePickerOptions
): Promise<NativePickerResult | null> => {
  try {
    const ctx = getContext();
    const { allowedFormats, multiSelect = false, maxCount = 1 } = options;

    const response = await ctx.handlers.openFileChooser({
      allowedFormats,
      multiSelectEnabled: multiSelect,
      maxCount,
    });

    if (!response?.resources || response.resources.length === 0) {
      return { files: [] };
    }

    // Map resources to our file format
    const files: NativePickerFile[] = response.resources.map((resource) => {
      const r = resource as PickerResource;
      const url = getPickerResourceUrl(r);
      const isVideo = isPickerResourceVideo(r);

      return {
        url,
        id: r.id ?? '',
        type: isVideo ? 'video' : 'photo',
      };
    });

    return { files };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[openNativePicker] Failed to open native picker:', error);
    return null;
  }
};

export type { NativePickerOptions, NativePickerFile, NativePickerResult };

export default openNativePicker;
